import { ColTypeDef } from "@ag-grid-community/core";
import { ToastId } from "@chakra-ui/react";
import { formatISO, isValid, parse, parseISO } from "date-fns";
import { useMemo } from "react";
import { MaybernTableAmountCellEditor } from "shared/components/table/maybern/MaybernTableAmountCellEditor";
import { MaybernTableAmountCellEditorRenderer } from "shared/components/table/maybern/MaybernTableAmountCellEditorRenderer";
import { MaybernTableCellRenderer } from "shared/components/table/maybern/MaybernTableCellRenderer";
import { MaybernTableDateCellEditor } from "shared/components/table/maybern/MaybernTableDateCellEditor";
import { MaybernTableDateCellEditorRenderer } from "shared/components/table/maybern/MaybernTableDateCellEditorRenderer";
import {
  MaybernTableDropdownCellEditor,
  MaybernTableDropdownCellEditorParams,
} from "shared/components/table/maybern/MaybernTableDropdownCellEditor";
import { MaybernTableDropdownCellEditorRenderer } from "shared/components/table/maybern/MaybernTableDropdownCellEditorRenderer";
import { MaybernTableDropdownCellRenderer } from "shared/components/table/maybern/MaybernTableDropdownCellRenderer";
import {
  MaybernTableTextCellEditor,
  MaybernTableTextCellEditorRenderer,
} from "shared/components/table/maybern/MaybernTableTextCellEditor";
import {
  maybernTableDecimalSumAggFunc,
  maybernTableMergeHeaderClass,
} from "shared/components/table/maybern/MaybernTableUtils";
import { UseToastProps } from "shared/components/toast/useToast";
import {
  formatMoneyForTable,
  formatMoneyWithoutCurrencySymbol,
} from "shared/utils/MoneyUtils";

export const useGetMaybernColumns = (
  toaster: (props: UseToastProps) => ToastId,
): {
  [key: string]: ColTypeDef;
} =>
  useMemo(
    () => ({
      dropdown: {
        cellRenderer: MaybernTableDropdownCellRenderer,
        cellClassRules: {
          "mb-cell-dropdown": () => true,
        },
      },
      dropdownEditable: {
        cellRenderer: MaybernTableDropdownCellEditorRenderer,
        cellEditor: MaybernTableDropdownCellEditor,
        editable: true,
        cellClassRules: {
          "mb-cell-dropdown": () => true,
        },
        valueFormatter: (params) => {
          const dropdownProps = params.colDef
            .cellEditorParams as MaybernTableDropdownCellEditorParams;
          return (
            dropdownProps.options.find((it) => it.value === params.value)
              ?.label ?? params.value
          );
        },
        valueParser: (params) => {
          const dropdownProps = params.colDef
            .cellEditorParams as MaybernTableDropdownCellEditorParams;
          if (!params.newValue) {
            return dropdownProps.emptyValue || null;
          }
          return (
            dropdownProps.options.find(
              (it) =>
                it.label?.toLowerCase() ===
                (params.newValue as string | null)?.toLowerCase(),
            )?.value ?? ""
          );
        },
      },
      dateEditable: {
        cellRenderer: MaybernTableDateCellEditorRenderer,
        cellEditor: MaybernTableDateCellEditor,
        editable: true,
        cellClassRules: {
          "mb-cell-date": () => true,
        },
        valueParser: (params) => {
          if (!params.newValue) {
            return "";
          }

          const parsedIso = parseISO(params.newValue);

          if (isValid(parsedIso)) {
            return formatISO(parsedIso, { representation: "date" });
          }

          const parsed = parse(params.newValue, "M/d/yyyy", new Date());

          if (isValid(parsed) && parsed.getFullYear() > 1900) {
            return formatISO(parsed, { representation: "date" });
          }
          const parsed2 = parse(params.newValue, "M/d/yy", new Date());
          if (isValid(parsed2) && parsed2.getFullYear() > 1900) {
            return formatISO(parsed2, { representation: "date" });
          }
          toaster({
            variant: "error",
            message:
              "We couldn't parse the date. Please enter a valid date in the format of MM/DD/YYYY.",
          });
          return "";
        },
      },
      moneyEditable: {
        cellRenderer: MaybernTableAmountCellEditorRenderer,
        cellEditor: MaybernTableAmountCellEditor,
        headerClass: "mb-header-value-type-money",
        cellClassRules: {
          "mb-cell-value-type-money": () => true,
        },
        initialWidth: 200,
        cellDataType: "money",
        valueParser: (params) => formatMoneyForTable(params.newValue),
        valueFormatter: (params) =>
          params.value
            ? formatMoneyWithoutCurrencySymbol(params.value, undefined, "")
            : "",
      },
      money: {
        headerClass: maybernTableMergeHeaderClass("mb-header-value-type-money"),
        cellClassRules: {
          "mb-cell-value-type-money": () => true,
        },
        initialWidth: 200,
        cellDataType: "money",
        aggFunc: maybernTableDecimalSumAggFunc,
        valueFormatter: (params) => {
          if (!params.data) {
            const aggData: Record<string, string> | undefined =
              params.node?.aggData;
            if (aggData && params.colDef.field) {
              return aggData[params.colDef.field];
            }
            return "";
          }
          return formatMoneyWithoutCurrencySymbol(params.value, undefined, "");
        },
      },
      moneyInput: {
        headerClass: maybernTableMergeHeaderClass("mb-header-value-type-money"),
        cellRenderer: MaybernTableCellRenderer,
      },
      textEditable: {
        cellRenderer: MaybernTableTextCellEditorRenderer,
        cellEditor: MaybernTableTextCellEditor,
        initialWidth: 200,
      },
    }),
    [toaster],
  );
